import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Spinner, Text, VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

/** LiveKit transport must be reconnecting this long before fullscreen overlay. */
export const TRANSPORT_RECONNECT_OVERLAY_DELAY_MS = 5_000;

export type ReconnectOverlayProps = {
  visible?: boolean;
};

/**
 * True after {@link TRANSPORT_RECONNECT_OVERLAY_DELAY_MS} of continuous reconnecting.
 * Shorter LiveKit blips stay in exam chrome (compact banner only).
 */
export function useTransportReconnectOverlayVisible(isReconnecting: boolean): boolean {
  const [visible, setVisible] = useState(false);
  const sinceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isReconnecting) {
      sinceRef.current = null;
      setVisible(false);
      return undefined;
    }

    const since = sinceRef.current ?? Date.now();
    sinceRef.current = since;
    const elapsed = Date.now() - since;

    if (elapsed >= TRANSPORT_RECONNECT_OVERLAY_DELAY_MS) {
      setVisible(true);
      return undefined;
    }

    setVisible(false);
    const timer = setTimeout(
      () => setVisible(true),
      TRANSPORT_RECONNECT_OVERLAY_DELAY_MS - elapsed,
    );
    return () => clearTimeout(timer);
  }, [isReconnecting]);

  return visible;
}

/** Transport disconnect / reconnecting overlay (§3.6). */
export function ReconnectOverlay({ visible = false }: ReconnectOverlayProps) {
  const { t } = useTranslation();

  if (!visible) return null;

  return (
    <Box
      testID="oral-exam-reconnect-overlay"
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="$bgOverlay"
      zIndex={10}
      justifyContent="center"
      alignItems="center"
      px="$6"
    >
      <VStack
        space="md"
        alignItems="center"
        maxWidth={420}
        bg="$bgCard"
        p="$6"
        borderRadius="$lg"
        softShadow="3"
      >
        <Spinner size="large" color="$brandBase" />
        <Text size="lg" fontWeight="semibold" color="$textHeading" textAlign="center">
          {t('oralExam.reconnect.title', { defaultValue: 'Reconnecting…' })}
        </Text>
        <Text size="sm" color="$textBody" textAlign="center">
          {t('oralExam.reconnect.body', {
            defaultValue: 'Your place is saved. The clock is paused on the server.',
          })}
        </Text>
        <Text size="sm" color="$textSubtle" textAlign="center">
          {t('oralExam.reconnect.hint', {
            defaultValue: 'Trying to restore your connection…',
          })}
        </Text>
      </VStack>
    </Box>
  );
}
